import { useTranslation } from "react-i18next"

import { useWhatsNew } from "@/hooks/use-whats-new"

import { WhatsNewDialogShell } from "./whats-new-dialog-shell"

type WhatsNewPreviewDialogProps = {
  /** Version string to preview. Required — the dialog only renders when set. */
  version: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Admin preview wrapper around `WhatsNewDialogShell`.
 *
 * Calls `/api/whats-new?version=…&preview=1` (admin-gated server-side) and
 * renders whatever comes back, *without* touching the user-facing Zustand
 * store or writing `whats_new_last_seen_version`. Two consequences worth
 * noting:
 *   - The user-popup dialog (`WhatsNewDialog`) can be mounted at the same
 *     time without state collisions; they share no module-level state.
 *   - Acknowledging the preview only closes the preview — the user's
 *     "last seen" pointer is never updated from this path.
 */
export function WhatsNewPreviewDialog({ version, open, onOpenChange }: WhatsNewPreviewDialogProps) {
  const { t, i18n } = useTranslation()
  const { data, isPending } = useWhatsNew(i18n.language, version)

  // Title: include the version, plus a fallback-locale hint if the backend
  // served the default locale instead of the requested one.
  const baseTitle = t("pages.whats_new.title")
  const requested = data?.requestedLocale
  const resolved = data?.resolvedLocale
  const usedFallback = Boolean(requested) && Boolean(resolved) && requested !== resolved
  const titleSuffix =
    usedFallback && requested && resolved
      ? t(
          "pages.whats_new.preview_locale_fallback",
          " — {{version}} ({{resolved}}, fallback from {{requested}})",
          { version, resolved, requested }
        )
      : ` — ${version}`

  return (
    <WhatsNewDialogShell
      isLoading={isPending}
      items={data?.items ?? null}
      markdown={data?.markdown ?? null}
      open={open}
      title={`${baseTitle}${titleSuffix}`}
      emptyFallback={
        <p className="text-muted-foreground italic">
          {t(
            "pages.whats_new.preview_empty",
            "No content available for {{version}} ({{locale}}).",
            { version, locale: i18n.language }
          )}
        </p>
      }
      onAcknowledge={() => onOpenChange(false)}
      onOpenChange={onOpenChange}
    />
  )
}
